import { RootState, SetState } from '@/store/useStore.ts';
import { emptySliceError } from '@/store/slices/sliceError';
import {
  ClientStatusPayload,
  GameContext,
  GameContextPayload,
  GameErrorPayload,
  GamePayload,
  GamePayloadPayload,
  GameSlice,
  GameStatusPayload,
  GameStoreParts,
  ResetAllGamePartsOptions,
} from './types';

const defaultGameStatus = {
  gameFetchStarted: false,
  gameFetchSuccess: false,
  gameFetchFailed: false,
  gameFetchErrorCode: null,
  gameLoadStarted: false,
  gameLoadSuccess: false,
  gameLoadProgress: 0,
  internalLoadProgress: 0,
  // Hidden testing flag
  hiddenTestingFlags: null,

  overallStatus: null,

  // Overall maintenance mode
  maintenanceMode: false,

  forceSwjBar: false,
  latestAuthenticationTokenUpdated: null,
  jackpotReconcileRunning: false,
  jackpotGlsPrewarmContextReceived: false,
};

const defaultGamePayload: GamePayload = {
  glsInitializeUrl: null,
  handshakeLaunchEpoch: null,
};

const defaultClientStatus = {
  loaded: false,
};

const defaultGameContext: GameContext = {
  rgi: null,
  brand: null,
  gameName: null,
  gameId: null,
  provider: null,
  glsLaunchProvider: null,
  providerUid: null,
  providerRef: null,
  gameUid: null,
  gameStudio: null,
  region: null,
  directLaunchParams: null,
  showNetPosition: null,
  unpausable: null,
  superclass: null,
  launchMethod: 'traditional-iframe',
  gamingSessionTrackingId: undefined,
  glsRequestId: null,
  isShortLinkLaunch: false,
  launchGeoProduct: 'UNKNOWN',
  geoProductRetryAttempted: false,
};

const defaults = {
  [GameStoreParts.gameContext]: defaultGameContext,
  [GameStoreParts.clientStatus]: defaultClientStatus,
  [GameStoreParts.gameStatus]: defaultGameStatus,
  [GameStoreParts.gamePayload]: defaultGamePayload,
  [GameStoreParts.gameError]: emptySliceError(),
};

const shouldPreserveLaunchHandshakeFields = (
  handshakeLaunchEpoch: number | null,
  options?: ResetAllGamePartsOptions,
): boolean => {
  if (
    options?.preserveGamePayloadKeys == null &&
    options?.preserveClientStatusKeys == null
  ) {
    return false;
  }

  if (options.launchHandshakeEpochGate === undefined) {
    return true;
  }

  return handshakeLaunchEpoch === options.launchHandshakeEpochGate;
};

const mergePreservedSliceFields = <T extends Record<string, unknown>>(
  current: T,
  defaultSlice: T,
  keys: readonly (keyof T)[] | undefined,
  preserve: boolean,
): T => {
  const next = { ...defaultSlice };

  if (!preserve || keys == null) {
    return next;
  }

  for (const key of keys) {
    next[key] = current[key];
  }

  return next;
};

export const gameSlice = ({ set }: SetState<RootState>): GameSlice => ({
  gameResetRevision: 0,
  gameContext: { ...defaults[GameStoreParts.gameContext] },
  gameStatus: { ...defaults[GameStoreParts.gameStatus] },
  clientStatus: { ...defaults[GameStoreParts.clientStatus] },
  gamePayload: { ...defaults[GameStoreParts.gamePayload] },
  gameError: { ...defaults[GameStoreParts.gameError] },

  setGameContext: (newState: GameContextPayload) =>
    set(
      (state: RootState) => ({
        ...state,
        gameContext: {
          ...state.gameContext,
          ...newState,
        },
      }),
      false,
      'game/setGameContext',
    ),

  setGameStatus: (newState: GameStatusPayload) =>
    set(
      (state: RootState) => ({
        ...state,
        gameStatus: {
          ...state.gameStatus,
          ...newState,
        },
      }),
      false,
      'game/setGameStatus',
    ),

  setClientStatus: (newState: ClientStatusPayload) =>
    set(
      (state: RootState) => ({
        ...state,
        clientStatus: {
          ...state.clientStatus,
          ...newState,
        },
      }),
      false,
      'game/setClientStatus',
    ),

  setGamePayload: (newState: GamePayloadPayload) =>
    set(
      (state: RootState) => ({
        ...state,
        gamePayload: {
          ...state.gamePayload,
          ...newState,
        },
      }),
      false,
      'game/setGamePayload',
    ),

  setGameError: (newState: GameErrorPayload) =>
    set(
      (state: RootState) => ({
        ...state,
        gameError: {
          ...state.gameError,
          ...newState,
        },
      }),
      false,
      'game/setGameError',
    ),

  resetGamePart: (part: GameStoreParts, options?: ResetAllGamePartsOptions) =>
    set(
      (state) => ({
        ...state,
        [part]: { ...defaults[part] },
        gameResetRevision: options?.skipRevisionBump
          ? state.gameResetRevision
          : state.gameResetRevision + 1,
      }),
      false,
      `game/reset/${part}`,
    ),

  resetAllGameParts: (options?: ResetAllGamePartsOptions) =>
    set(
      (state) => {
        const preserveHandshake = shouldPreserveLaunchHandshakeFields(
          state.gamePayload.handshakeLaunchEpoch,
          options,
        );

        return {
          ...state,
          gameContext: { ...defaults[GameStoreParts.gameContext] },
          clientStatus: mergePreservedSliceFields(
            state.clientStatus,
            defaults[GameStoreParts.clientStatus],
            options?.preserveClientStatusKeys,
            preserveHandshake,
          ),
          gameStatus: { ...defaults[GameStoreParts.gameStatus] },
          gamePayload: mergePreservedSliceFields(
            state.gamePayload,
            defaults[GameStoreParts.gamePayload],
            options?.preserveGamePayloadKeys,
            preserveHandshake,
          ),
          gameError: { ...defaults[GameStoreParts.gameError] },
          gameResetRevision: state.gameResetRevision + 1,
        };
      },
      false,
      'game/resetGame',
    ),
});
